/**
 * useBuoyAvailability - Custom Hook for Buoy Availability Data Management
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Manages fetching and caching of buoy availability data,
 * separating this concern from the main dashboard component.
 * 
 * Responsibilities:
 * - Fetch complete availability data (buoys + sensors + fields)
 * - Manage loading and error states
 * - Handle time range changes and data refresh
 * - Provide clean API for availability data access
 */

import { useState, useEffect } from 'react';
import { getCompleteAvailability } from '../../../../services/influxService';

export function useBuoyAvailability(timeRange, allowedBuoys, timeStop = null) {
  const [availableBuoys, setAvailableBuoys] = useState([]);
  const [perBuoyAvailability, setPerBuoyAvailability] = useState({});
  const [buoysLoading, setBuoysLoading] = useState(false);
  const [buoysError, setBuoysError] = useState(null);

  // Load complete availability data (buoys + sensors + fields) in one efficient query
  useEffect(() => {
    let cancelled = false;
    setBuoysLoading(true);
    setBuoysError(null);
    setAvailableBuoys([]);
    setPerBuoyAvailability({});

    (async () => {
      try {
        const completeData = await getCompleteAvailability(timeRange, allowedBuoys, timeStop);
        
        if (!cancelled) {
          setAvailableBuoys(completeData.availableBuoys);
          setPerBuoyAvailability(completeData.perBuoyAvailability);
        }
      } catch (e) {
        if (!cancelled) {
          setBuoysError(e?.message || 'Failed to load data');
          setAvailableBuoys([]);
          setPerBuoyAvailability({});
        }
        console.error('getCompleteAvailability() error:', e);
      } finally {
        if (!cancelled) setBuoysLoading(false);
      }
    })();
    
    return () => { cancelled = true };
  }, [timeRange, allowedBuoys, timeStop]);

  return {
    availableBuoys,
    perBuoyAvailability,
    buoysLoading,
    buoysError
  };
}